import axios from "axios";
import { getReferralCode } from "@/lib/referral";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

api.interceptors.request.use((cfg) => {
  const token = localStorage.getItem("reelens_token");
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

export const auth = {
  register: (data) => api.post("/auth/register", data).then((r) => r.data),
  login: (data) => api.post("/auth/login", data).then((r) => r.data),
  me: () => api.get("/auth/me").then((r) => r.data),
};

export const analyses = {
  preview: (instagram_url, email) =>
    api.post("/analyses/preview", { instagram_url, email }).then((r) => r.data),
  init: (instagram_url, email, phone, wa_opt_in) => {
    // Capture attribution once per session — UTMs + referrer from the first
    // landing URL. Persist to sessionStorage so it survives client-side nav.
    let attribution = {};
    try {
      if (typeof window !== "undefined") {
        const cached = sessionStorage.getItem("reelens_attribution");
        if (cached) {
          attribution = JSON.parse(cached);
        } else {
          const p = new URLSearchParams(window.location.search);
          attribution = {
            utm_source: p.get("utm_source") || undefined,
            utm_medium: p.get("utm_medium") || undefined,
            utm_campaign: p.get("utm_campaign") || undefined,
            utm_content: p.get("utm_content") || undefined,
            utm_term: p.get("utm_term") || undefined,
            referrer: (document.referrer || "").slice(0, 500) || undefined,
          };
          // Strip undefined so backend gets a clean payload
          Object.keys(attribution).forEach(k => attribution[k] === undefined && delete attribution[k]);
          sessionStorage.setItem("reelens_attribution", JSON.stringify(attribution));
        }
      }
    } catch { /* localStorage disabled — silently skip */ }
    const body = { instagram_url, email, ...attribution };
    if (phone) body.phone = phone;
    if (wa_opt_in) body.wa_opt_in = true;
    const ref_code = getReferralCode();
    if (ref_code) body.ref_code = ref_code;
    return api.post("/analyses/init", body).then((r) => r.data);
  },
  previewStatus: (id) =>
    api.get(`/analyses/${id}/preview-status`).then((r) => r.data),
  resume: (id, token) =>
    api.get(`/analyses/${id}/resume`, { params: { token } }).then((r) => r.data),
  getPublic: (id, token) =>
    api.get(`/analyses/${id}/public`, { params: { token } }).then((r) => r.data),
  pdfPublicUrl: (id, token) =>
    `${API}/analyses/${id}/pdf/public?token=${encodeURIComponent(token)}`,
  waPreviewLink: (id, token) =>
    api.get(`/analyses/${id}/wa-preview-link`, { params: { token } }).then((r) => r.data),
  waReportLink: (id, token) =>
    api.get(`/analyses/${id}/wa-report-link`, { params: { token } }).then((r) => r.data),
};

export const stats = {
  publicCounter: () => api.get("/stats/public-counter").then((r) => r.data),
};

export const payments = {
  createOrder: (analysis_id, unlock_token) =>
    api.post("/payments/razorpay/order", { analysis_id, unlock_token }).then((r) => r.data),
  verify: (data) =>
    api.post("/payments/razorpay/verify", data).then((r) => r.data),
  createBundle: (analysis_id, unlock_token) =>
    api.post("/payments/razorpay/bundle", { analysis_id, unlock_token }).then((r) => r.data),
  verifyBundle: (data) =>
    api.post("/payments/razorpay/bundle/verify", data).then((r) => r.data),
};

export const growth = {
  subscriptionForAnalysis: (id, token) =>
    api.get(`/analyses/${id}/subscription`, { params: { token } }).then((r) => r.data),
  createSubscription: (analysis_id, unlock_token) =>
    api.post("/subscriptions/create", { analysis_id, unlock_token }).then((r) => r.data),
  verifySubscription: (subId, data) =>
    api.post(`/subscriptions/${subId}/verify`, data).then((r) => r.data),
  getSubscription: (subId, token) =>
    api.get(`/subscriptions/${subId}`, { params: { token } }).then((r) => r.data),
  cancelSubscription: (subId, token) =>
    api.post(`/subscriptions/${subId}/cancel`, { token }).then((r) => r.data),
  myReferral: (id, token) =>
    api.get(`/analyses/${id}/referral`, { params: { token } }).then((r) => r.data),
  referralVisit: (code) =>
    api.post(`/referrals/${code}/visit`).then((r) => r.data),
  referralDashboard: (code, token) =>
    api.get(`/referrals/${code}`, { params: { token } }).then((r) => r.data),
  saveUpi: (code, token, upi_id) =>
    api.post(`/referrals/${code}/upi`, { token, upi_id }).then((r) => r.data),
};
